/**
 * MathJax 动态加载器（CDN）。
 * 试卷内容里的数学有两种：内联 MathML（SAT/AP）与 LaTeX（practice，迁移时已转为 \(...\) / \[...\]）。
 * 用 MathJax 的 tex-mml-chtml 组件同时渲染 MathML 与 LaTeX；不启用 $ 定界符以免误伤金额文本。
 * 单例：首次调用时注入 CDN script；typeset 对指定元素重新排版。
 */

const MATHJAX_SRC = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';

let loadPromise: Promise<void> | null = null;

declare global {
  interface Window {
    MathJax?: any;
  }
}

/** 确保 MathJax 已加载（幂等） */
export function ensureMathJax(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // 已存在则直接完成
    if (window.MathJax?.typesetPromise) {
      resolve();
      return;
    }
    // 全局配置需在脚本加载前设置
    window.MathJax = {
      tex: {
        // 只认明确的 LaTeX 定界符 \(...\) / \[...\]；不认 $ / $$，
        // 避免 SAT/AP HTML 里的美元金额（如 $45 ... $10.75）被误当公式。
        // practice 的 LaTeX 在迁移预处理阶段已把 $...$ 转成 \(...\)。
        inlineMath: [['\\(', '\\)']],
        displayMath: [['\\[', '\\]']],
        processEscapes: true,
      },
      options: {
        // 不因个别错误中断整页排版
        enableMenu: false,
      },
      startup: {
        typeset: false, // 不自动排版整页，由我们按元素调用
      },
    };
    const script = document.createElement('script');
    script.src = MATHJAX_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load MathJax'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

/** 对指定元素内的 MathML 排版；MathJax 未就绪时静默跳过（降级为原始 MathML） */
export async function typesetElement(el: HTMLElement): Promise<void> {
  try {
    await ensureMathJax();
    if (window.MathJax?.typesetPromise) {
      await window.MathJax.typesetPromise([el]);
    }
  } catch {
    // 加载失败则保持原始 MathML（浏览器原生尽力渲染）
  }
}
